import React, { useCallback } from 'react';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import { v4 as uuidv4 } from 'uuid';
import useGetAttachmentUploadUrl from '#/common/components/AttachmentUpload/hooks/useAttachmentUploadUrlMutation';
import useAttachmentCreate from '#/common/components/AttachmentUpload/hooks/useAttachmentCreateMutation';
import useImportAssetFileMutation from '../hooks/useImportAssetFileMutation';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';
import { inUseStatuses } from '#/modules/asset/model/utils/constants';

export type MappingConfig =
  | string
  | { field?: string; defaultValue?: string; relatedTo?: string }
  | undefined;

export type FieldMapConfig = Record<string, MappingConfig>;

export type LocationLevelConfig = { name: MappingConfig; id?: string };

export type ObjectImportConfig = {
  model: string;
  thirdPartyId: string;
  thirdPartyIdSource?: string;
  allowUnknownRecords?: boolean;
  allowDuplicateKeys?: boolean;
  columnMap: FieldMapConfig;
  hierarchy?: {
    country: LocationLevelConfig;
    state: LocationLevelConfig;
    city: LocationLevelConfig;
  };
  updateColumns?: string[];
  uniqueForTenant?: boolean;
  onlyForMapping?: boolean;
  fixedId?: string;
};

interface SubmitModalProps {
  visible: boolean;
  uploadedFile: File | null;
  mapping: Record<string, string>;
  uniqueField: string;
  selectedTenant?: Tenant;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: (error: unknown) => void;
}

const SubmitModal: React.FC<SubmitModalProps> = ({
  visible,
  uploadedFile,
  mapping,
  uniqueField,
  selectedTenant,
  onHiding,
  onImportStart,
  onSuccess,
  onFailed,
}) => {
  const { getAttachmentUploadUrls } = useGetAttachmentUploadUrl();
  const { createAttachment } = useAttachmentCreate();
  const { importAssetFile } = useImportAssetFileMutation();

  // Generate the import config based on the mapping
  const generateImportConfig = useCallback((): string => {
    if (!selectedTenant || !mapping || Object.keys(mapping).length === 0) {
      return '';
    }

    const isColumnMapped = (field: string) =>
      !!mapping[field] && mapping[field] !== unknownMappingValue.key;

    const tenantImportConfig: ObjectImportConfig = {
      model: 'TenantModel',
      fixedId: selectedTenant.id,
      thirdPartyId: 'dataSourceId',
      columnMap: {
        dataSourceId: { defaultValue: selectedTenant.dataSourceId ?? '' },
      },
      onlyForMapping: true,
    };

    const manufacturerImportConfig: ObjectImportConfig = {
      model: 'ManufacturerModel',
      thirdPartyId: 'name',
      allowUnknownRecords: true,
      allowDuplicateKeys: true,
      columnMap: {
        name: mapping['manufacturer'],
        thirdPartyId: mapping['manufacturer'],
        contactPerson: { defaultValue: '' },
        inUseStatus: { defaultValue: inUseStatuses.inUse.key },
        tenantId: {
          relatedTo: 'TenantModel',
        },
      },
      updateColumns: ['name'],
      uniqueForTenant: true,
    };

    const assetTypeImportConfig: ObjectImportConfig = {
      model: 'AssetTypeModel',
      thirdPartyId: 'name',
      allowUnknownRecords: true,
      allowDuplicateKeys: true,
      columnMap: {
        name: mapping['asset_type'],
        thirdPartyId: mapping['asset_type'],
        defaultRiskScore: { defaultValue: '0' },
        category: { defaultValue: 'other' },
        inUseStatus: { defaultValue: inUseStatuses.inUse.key },
        tenantId: {
          relatedTo: 'TenantModel',
        },
      },
      updateColumns: ['name'],
      uniqueForTenant: true,
    };

    const modelImportConfig: ObjectImportConfig = {
      model: 'ModelModel',
      thirdPartyId: 'name',
      allowUnknownRecords: true,
      allowDuplicateKeys: true,
      columnMap: {
        name: mapping['model'],
        thirdPartyId: mapping['model'],
        inUseStatus: { defaultValue: inUseStatuses.inUse.key },
        tenantId: {
          relatedTo: 'TenantModel',
        },
        manufacturerId: {
          relatedTo: 'ManufacturerModel',
        },
        assetTypeId: {
          relatedTo: 'AssetTypeModel',
        },
      },
      updateColumns: ['name', 'manufacturerId', 'assetTypeId'],
      uniqueForTenant: true,
    };

    const locationImportConfig: ObjectImportConfig = {
      model: 'LocationModel',
      thirdPartyId: 'name',
      columnMap: {
        name: mapping['location_building'],
        tenantId: { defaultValue: selectedTenant.id },
        address: isColumnMapped('location_address') ? mapping['location_address'] : undefined,
      },
      hierarchy: {
        country: { name: mapping['location_country'] },
        state: { name: mapping['location_state'] },
        city: { name: mapping['location_city'] },
      },
      updateColumns: ['name'],
    };

    const assetImportConfig: ObjectImportConfig = {
      model: 'AssetModel',
      thirdPartyId: 'thirdPartyId2',
      columnMap: {
        thirdPartyId2:
          uniqueField === 'serial_number' ? mapping['serial_number'] : mapping['asset_tag'],
        // Mandatory fields
        assetTag: mapping['asset_tag'],
        serialNumber: isColumnMapped('serial_number')
          ? mapping['serial_number']
          : { defaultValue: unknownMappingValue.label },
        state: { defaultValue: 'installed' },
        description: isColumnMapped('description')
          ? { field: mapping['description'], defaultValue: '' }
          : { defaultValue: unknownMappingValue.label },
        // Optional fields
        installDate: isColumnMapped('install_date') ? mapping['install_date'] : undefined,
        condition: isColumnMapped('condition') ? mapping['condition'] : undefined,
        acquisitionCostValue: isColumnMapped('acquisition_cost_value')
          ? mapping['acquisition_cost_value']
          : undefined,
        maintenanceCostValue: isColumnMapped('maintenance_cost_value')
          ? mapping['maintenance_cost_value']
          : undefined,
        usefulLife: isColumnMapped('useful_life') ? mapping['useful_life'] : undefined,
        // Related entities
        tenantId: {
          relatedTo: 'TenantModel',
        },
        modelId: {
          relatedTo: 'ModelModel',
        },
        makeId: {
          relatedTo: 'ManufacturerModel',
        },
        assetTypeId: {
          relatedTo: 'AssetTypeModel',
        },
        locationId: {
          relatedTo: 'LocationModel',
        },
      },
      updateColumns: [
        'assetTag',
        'serialNumber',
        'description',
        'installDate',
        'condition',
        'acquisitionCostValue',
        'maintenanceCostValue',
        'usefulLife',
        'modelId',
        'makeId',
        'assetTypeId',
        'locationId',
      ],
      uniqueForTenant: true,
    };

    // Base config structure that mirrors backend's salesForceAssetConfig
    const config: ObjectImportConfig[] = [
      tenantImportConfig,
      manufacturerImportConfig,
      assetTypeImportConfig,
      modelImportConfig,
      locationImportConfig,
      assetImportConfig,
    ];

    return JSON.stringify(config);
  }, [uniqueField, mapping, selectedTenant]);

  // Upload CSV file as an attachment and get the fileId to send to the backend for processing
  const uploadInputFile = useCallback(
    async (file: File | null): Promise<string | undefined> => {
      if (!file) return;

      const relatedTable = 'data_import'; // TODO: No such table yet. Replace with correct related table when created.
      const relatedItem = uuidv4(); // TODO: Using a random UUID. Replace with correct related item ID when table is created.

      const uploadInfos = await getAttachmentUploadUrls(
        [
          {
            fileName: file.name,
            size: file.size,
            contentType: file.type,
            relatedTable,
            relatedItem,
            type: 'general',
          },
        ],
        selectedTenant?.id,
      );

      const uploadInfo = Array.isArray(uploadInfos)
        ? uploadInfos.find((u: any) => u?.uploadUrl && u.uploadName)
        : undefined;

      if (!uploadInfo?.uploadUrl || !uploadInfo?.uploadName) {
        throw new Error('Failed to get upload URL');
      }

      const uploadResponse = await fetch(uploadInfo.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      const attachment = await createAttachment({
        fileName: String(uploadInfo.uploadName).split('/').pop() || '',
        size: file.size,
        mimeType: file.type,
        relatedTable,
        relatedItem,
        type: 'general',
        path: uploadInfo.uploadName,
        originalFileName: uploadInfo.originalFileName,
        tenantId: selectedTenant?.id,
      });

      if (!attachment?.id || !attachment.downloadUrl) {
        throw new Error('Failed to create attachment record');
      }

      return attachment.id;
    },
    [getAttachmentUploadUrls, createAttachment, selectedTenant],
  );

  const insertNewAssets = useCallback(
    async (skipExisting: boolean) => {
      onHiding();
      onImportStart();

      try {
        // Upload the file to S3 bucket and get the fileId
        const fileId = await uploadInputFile(uploadedFile);
        if (!fileId) {
          throw new Error('Error uploading file');
        }

        // Call the actual file import on backend side
        const importConfig = generateImportConfig();
        const importResult = await importAssetFile({
          importConfig,
          fileId,
          skipExisting,
        });

        if (!importResult) {
          throw new Error('Import failed');
        }

        onSuccess(importResult);
      } catch (error) {
        onFailed(error);
        return;
      }
    },
    [selectedTenant, uploadedFile, mapping, generateImportConfig],
  );

  const handleOverwriteSkip = useCallback(async (): Promise<void> => {
    await insertNewAssets(true);
  }, [insertNewAssets]);

  const handleOverwriteConfirm = useCallback(async (): Promise<void> => {
    await insertNewAssets(false);
  }, [insertNewAssets]);

  return (
    <Modal
      visible={visible}
      title="Overwrite Data of Existing Assets"
      width={640}
      height="auto"
      onHiding={onHiding}
      content={
        <>
          <div>
            If there are already existing asset records do you want to overwrite them with the new
            data from this file or skip them?
          </div>
          <div>Overwriting the records means that all changes made before will be lost.</div>
        </>
      }
      actions={[
        <TMMButton key="cancel" label="Skip" type="normal" onClick={handleOverwriteSkip} />,
        <TMMButton
          key="ok"
          label="Overwrite Data"
          type="submit"
          onClick={handleOverwriteConfirm}
        />,
      ]}
    />
  );
};

export default SubmitModal;
